import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { format, addDays } from 'date-fns';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FormControlLabel from '@mui/material/FormControlLabel';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Types
interface ChecklistItem {
    Name: string;
    Checked: boolean;
    IsCustom?: boolean;
    id?: string;
}

interface TimeSlot {
    timeSlotID: number;
    startTime: string;
    endTime: string;
    totalQualifiedInspectors: number;
    pendingRequests: number;
    availableCapacity: number;
    availableInspectors: number[];
}

interface Inspector {
    inspectorID: number;
    name: string;
    email: string;
    qualifiedSubcodes: number[];
    availableTimeSlots: number[];
    status: string;
    details?: any;
}

/**
 * ReassignInspectorModal Component
 * 
 * Modal for front desk managers to reassign inspectors to scheduled inspections
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {Object} props.applicationData - Object containing application data
 * @param {function} props.onReassignSuccess - Callback after successful reassignment
 */
const ReassignInspectorModal = ({
    open,
    onClose,
    applicationData,
    onReassignSuccess
}) => {
    // eslint-disable-next-line no-console
    console.log("applicationData", applicationData)
    // State for form values
    const [inspector, setInspector] = React.useState('');
    const [selectedDate, setSelectedDate] = React.useState('');
    const [selectedTimeSlot, setSelectedTimeSlot] = React.useState(null);
    const [notes, setNotes] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [inspectors, setInspectors] = React.useState<Inspector[]>([]);
    const [fetchingInspectors, setFetchingInspectors] = React.useState(false);
    const [timeSlots, setTimeSlots] = React.useState<TimeSlot[]>([]);
    const [fetchingTimeSlots, setFetchingTimeSlots] = React.useState(false);

    // Checklist related state
    const [checklistItems, setChecklistItems] = React.useState<ChecklistItem[]>([]);
    const [fetchingChecklist, setFetchingChecklist] = React.useState(false);
    const [newChecklistItem, setNewChecklistItem] = React.useState('');
    const [checklistExpanded, setChecklistExpanded] = React.useState(false);

    // Get minimum date (tomorrow)
    const minDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');

    // Format time slot display
    const formatTimeSlot = (startTime, endTime) => {
        // Handle case where startTime or endTime might be objects
        const startTimeStr = typeof startTime === 'string' ? startTime :
            startTime?.StartTime || startTime?.startTime || '';
        const endTimeStr = typeof endTime === 'string' ? endTime :
            endTime?.EndTime || endTime?.endTime || '';

        const formatTime = (time) => {
            if (!time) return '';
            const [hours, minutes] = time.split(':');
            const hour12 = parseInt(hours) % 12 || 12;
            const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
            return `${hour12}:${minutes} ${ampm}`;
        };

        return `${formatTime(startTimeStr)} - ${formatTime(endTimeStr)}`;
    };

    // Fetch checklist items for the inspection type
    const fetchInspectionTypeChecklist = async (inspectionTypeID) => {
        if (!inspectionTypeID) return;

        setFetchingChecklist(true);
        try {
            const response = await httpRequest(
                'GET',
                `/construction/inspectionTypes/getType/${inspectionTypeID}`
            );

            if (response?.status === 'success' && response.data?.inspectionType) {
                const inspectionType = response.data.inspectionType;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const checklistArray = inspectionType.InspectionCheckList || [];

                // Convert checklist items to the required format
                const formattedChecklist = applicationData.InspectionCheckList?.map((item, index) => ({
                    Name: item.Name,
                    Checked: true,
                    IsCustom: item.IsCustom,
                    id: `default-${index}`
                }));

                setChecklistItems(formattedChecklist);
            } else {
                console.error('Error fetching inspection type checklist:', response);
                setChecklistItems([]);
            }
        } catch (error) {
            console.error('Error fetching inspection type checklist:', error);
            toast.error('Failed to load inspection checklist');
            setChecklistItems([]);
        } finally {
            setFetchingChecklist(false);
        }
    };

    // Fetch available time slots for selected date
    const fetchAvailableTimeSlots = async (inspectionDate) => {
        if (!inspectionDate || !applicationData?.SubcodeNameID) return;

        setFetchingTimeSlots(true);
        setTimeSlots([]);
        setSelectedTimeSlot(null);
        setInspector('');
        setInspectors([]);

        try {
            const formattedDate = format(new Date(inspectionDate), 'MM/dd/yyyy');

            const response = await httpRequest(
                'GET',
                `/construction/application/inspector/all-availability?InspectionDate=${formattedDate}&SubcodeID=${applicationData.SubcodeNameID}`
            );

            if (response?.status === 'success' && response.data?.availableTimeSlots) {
                setTimeSlots(response.data.availableTimeSlots);
            } else {
                console.error('Error fetching available time slots:', response);
                toast.error('No available time slots found for the selected date');
                setTimeSlots([]);
            }
        } catch (error) {
            console.error('Error fetching available time slots:', error);
            toast.error('Failed to load available time slots');
            setTimeSlots([]);
        } finally {
            setFetchingTimeSlots(false);
        }
    };

    // Fetch available inspectors for selected time slot
    const fetchAvailableInspectors = async (timeSlotID) => {
        if (!timeSlotID || !selectedDate || !applicationData?.SubcodeNameID) return;

        setFetchingInspectors(true);
        setInspectors([]);
        setInspector('');

        try {
            const formattedDate = format(new Date(selectedDate), 'MM/dd/yyyy');

            const response = await httpRequest(
                'GET',
                `/construction/application/inspector/available-inspectors-for-timeslot?InspectionDate=${formattedDate}&SubcodeID=${applicationData.SubcodeNameID}&TimeSlotID=${timeSlotID}`
            );

            if (response?.status === 'success' && response.data?.inspectors?.available) {
                setInspectors(response.data.inspectors.available);
            } else {
                console.error('Error fetching available inspectors:', response);
                toast.error('No available inspectors found for the selected time slot');
                setInspectors([]);
            }
        } catch (error) {
            console.error('Error fetching available inspectors:', error);
            toast.error('Failed to load available inspectors');
            setInspectors([]);
        } finally {
            setFetchingInspectors(false);
        }
    };

    // Handle date change
    const handleDateChange = (event) => {
        const newDate = event.target.value;
        setSelectedDate(newDate);
        if (newDate) {
            fetchAvailableTimeSlots(newDate);
        }
    };

    // Handle time slot selection
    const handleTimeSlotSelect = (timeSlot) => {
        setSelectedTimeSlot(timeSlot);
        fetchAvailableInspectors(timeSlot.timeSlotID);
    };

    // Fetch checklist when modal opens
    React.useEffect(() => {
        // eslint-disable-next-line no-console
        console.log("applicationData?.InspectorAssignments?.NoteForInspector", applicationData?.InspectorAssignments[0]?.NoteForInspector)
        if (open && applicationData?.InspectionTypeID) {
            fetchInspectionTypeChecklist(applicationData.InspectionTypeID);

            // Pre-populate notes if available
            if (applicationData?.InspectorAssignments[0]?.NoteForInspector) {
                // eslint-disable-next-line no-console
                console.log("applicationData?.InspectorAssignments?.NoteForInspector", applicationData?.InspectorAssignments?.NoteForInspector)
                setNotes(applicationData.InspectorAssignments[0].NoteForInspector);
            }
        }
    }, [open, applicationData?.InspectionTypeID]);

    // Handle checkbox change for checklist items
    const handleChecklistItemChange = (itemId, checked) => {
        setChecklistItems(prev =>
            prev.map(item =>
                item.id === itemId ? { ...item, Checked: checked } : item
            )
        );
    };

    // Add new custom checklist item
    const handleAddCustomChecklistItem = () => {
        if (!newChecklistItem.trim()) return;

        const newItem = {
            Name: newChecklistItem.trim(),
            Checked: false,
            IsCustom: true,
            id: `custom-${Date.now()}`
        };

        setChecklistItems(prev => [...prev, newItem]);
        setNewChecklistItem('');
    };

    // Remove custom checklist item
    const handleRemoveCustomChecklistItem = (itemId) => {
        setChecklistItems(prev => prev.filter(item => item.id !== itemId));
    };

    // Handle key press for adding new checklist item
    const handleNewItemKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleAddCustomChecklistItem();
        }
    };

    // Reset form and close modal
    const handleClose = () => {
        setInspector('');
        setSelectedDate('');
        setSelectedTimeSlot(null);
        setNotes('');
        setInspectors([]);
        setTimeSlots([]);
        setChecklistItems([]);
        setNewChecklistItem('');
        setChecklistExpanded(true);
        onClose();
    };

    // Handle form submission
    const handleReassignInspector = async () => {
        if (!inspector || !selectedTimeSlot || !selectedDate) {
            toast.error('Please select a date, time slot, and inspector');
            return;
        }

        setLoading(true);
        try {
            // Format checklist items for API
            const formattedChecklistItems = checklistItems.map(item => ({
                Name: item.Name,
                Checked: false,
                IsCustom: item.IsCustom || false
            }));

            const payload = {
                InspectionID: applicationData?.ID,
                InspectorID: inspector,
                AssignTimeSlot: selectedTimeSlot.timeSlotID,
                InspectionDate: selectedDate,
                NoteForInspector: notes,
                InspectionCheckList: formattedChecklistItems,
                IsReschedule: true
            };

            // eslint-disable-next-line no-console
            console.log("payload ---- ", payload)

            const response = await httpRequest('POST', '/construction/application/inspection_assign', payload);

            if (response?.status === 'success') {
                toast.success('Inspector reassigned successfully');
                onReassignSuccess();
                handleClose();
            } else {
                toast.error(response?.message || 'Failed to reassign inspector');
            }
        } catch (error) {
            console.error('Error reassigning inspector:', error);
            toast.error('An error occurred while reassigning inspector');
        } finally {
            setLoading(false);
        }
    };

    // Count selected checklist items
    const selectedChecklistCount = checklistItems.filter(item => item.Checked).length;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 1, sm: 2 },
                    maxHeight: { xs: '95vh', sm: '90vh' },
                    width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                }
            }}
        >
            {loading && <LoadingOverlay />}
            <DialogTitle
                sx={{
                    bgcolor: 'warning.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    textAlign: { xs: 'center', sm: 'left' },
                    p: { xs: 2, sm: 3 }
                }}
            >
                <SwapHorizIcon sx={{ mr: { xs: 0, sm: 1 } }} />
                <Typography variant="h6" component="div" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Reassign Inspector for Inspection Request
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
                {/* Application Info */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Application #:</strong> {applicationData?.ApplicationNumber || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Subcode:</strong> {applicationData?.SubcodeName || 'Building'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Title:</strong> {applicationData?.ApplicationDetails?.ApplicationTitle || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Applicant:</strong> {applicationData?.ApplicationDetails?.ApplicantName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Inspection Type:</strong> {applicationData?.InspectionType || 'N/A'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Alert>

                {/* Current Assignment Info */}
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                        Current Assignment Details
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Current Inspector:</strong> {applicationData?.InspectorDetails?.FirstName} {applicationData?.InspectorDetails?.LastName}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Current Date:</strong> {applicationData?.InspectorAssignments?.[0]?.InspectionDate ? format(new Date(applicationData.InspectorAssignments[0].InspectionDate), 'EEEE, MMMM dd, yyyy') : 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Current Time Slot:</strong> {applicationData?.InspectorAssignments?.[0]?.TimeSlot ?
                                    (() => {
                                        const timeSlot = applicationData.InspectorAssignments[0].TimeSlot;
                                        if (typeof timeSlot === 'string') return timeSlot;
                                        const startTime = timeSlot.StartTime || timeSlot.startTime;
                                        const endTime = timeSlot.EndTime || timeSlot.endTime;
                                        if (!startTime || !endTime) return 'N/A';
                                        const formatTime = (time) => {
                                            const [hours, minutes] = time.split(':');
                                            const hour12 = parseInt(hours) % 12 || 12;
                                            const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                                            return `${hour12}:${minutes} ${ampm}`;
                                        };
                                        return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                                    })() : 'N/A'
                                }
                            </Typography>
                        </Grid>
                    </Grid>
                </Alert>

                {/* Inspection Checklist Section */}
                <Accordion
                    expanded={checklistExpanded}
                    onChange={(e, isExpanded) => setChecklistExpanded(isExpanded)}
                    sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            bgcolor: 'primary.alpha12',
                            '&:hover': { bgcolor: 'primary.alpha24' }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <ChecklistRtlIcon color="primary" />
                            <Typography variant="h6" color="primary">
                                Inspection Checklist
                            </Typography>
                            {checklistItems.length > 0 && (
                                <Chip
                                    size="small"
                                    label={`${selectedChecklistCount}/${checklistItems.length} selected`}
                                    color={selectedChecklistCount > 0 ? 'primary' : 'default'}
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {fetchingChecklist ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                <CircularProgress size={24} sx={{ mr: 2 }} />
                                <Typography color="text.secondary">Loading checklist items...</Typography>
                            </Box>
                        ) : (
                            <>
                                {checklistItems.length > 0 ? (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                            Select the checklist items that need to be inspected:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                                            <FormGroup>
                                                {checklistItems.map((item) => (
                                                    <Box key={item.id} sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        py: 0.5,
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                        borderRadius: 1,
                                                        px: 1
                                                    }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={item.Checked}
                                                                    onChange={(e) => handleChecklistItemChange(item.id, e.target.checked)}
                                                                    size="small"
                                                                />
                                                            }
                                                            label={
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                                        color: item.Checked ? 'primary.main' : 'text.primary'
                                                                    }}
                                                                >
                                                                    {item.Name}
                                                                    {item.IsCustom && (
                                                                        <Chip
                                                                            size="small"
                                                                            label="Custom"
                                                                            color="secondary"
                                                                            variant="outlined"
                                                                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                                        />
                                                                    )}
                                                                </Typography>
                                                            }
                                                            sx={{ flex: 1, mr: 1 }}
                                                        />
                                                        {item.IsCustom && (
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRemoveCustomChecklistItem(item.id)}
                                                                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                            </FormGroup>
                                        </Paper>
                                    </Box>
                                ) : (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        No predefined checklist items found for this inspection type.
                                    </Alert>
                                )}

                                {/* Add Custom Checklist Item */}
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                        Add additional checklist items if needed:
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Enter additional checklist item..."
                                        value={newChecklistItem}
                                        onChange={(e) => setNewChecklistItem(e.target.value)}
                                        onKeyPress={handleNewItemKeyPress}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleAddCustomChecklistItem}
                                                        disabled={!newChecklistItem.trim()}
                                                        color="primary"
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </AccordionDetails>
                </Accordion>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Date Selection */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Select New Inspection Date"
                            type="date"
                            fullWidth
                            required
                            value={selectedDate}
                            onChange={handleDateChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: minDate,
                            }}
                            size="medium"
                            sx={{ mb: 2 }}
                        />
                    </Grid>

                    {/* Time Slot Selection */}
                    <Grid item xs={12}>
                        {selectedDate && (
                            <>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon />
                                    Available Time Slots for {format(new Date(selectedDate), 'EEEE, MMMM dd, yyyy')}
                                </Typography>

                                {fetchingTimeSlots ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                        <CircularProgress size={24} sx={{ mr: 2 }} />
                                        <Typography color="text.secondary">Loading available time slots...</Typography>
                                    </Box>
                                ) : timeSlots.length > 0 ? (
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        {timeSlots.map((slot) => (
                                            <Grid item xs={12} sm={6} md={4} key={slot.timeSlotID}>
                                                <Paper
                                                    sx={{
                                                        p: 2,
                                                        cursor: 'pointer',
                                                        border: selectedTimeSlot?.timeSlotID === slot.timeSlotID ? 2 : 1,
                                                        borderColor: selectedTimeSlot?.timeSlotID === slot.timeSlotID ? 'primary.main' : 'divider',
                                                        bgcolor: selectedTimeSlot?.timeSlotID === slot.timeSlotID ? 'primary.alpha12' : 'transparent',
                                                        '&:hover': {
                                                            bgcolor: 'action.hover',
                                                            borderColor: 'primary.main'
                                                        },
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                    onClick={() => handleTimeSlotSelect(slot)}
                                                >
                                                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                                        {formatTimeSlot(slot.startTime, slot.endTime)}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Chip
                                                            size="small"
                                                            label={`${slot.availableCapacity} Available`}
                                                            color={slot.availableCapacity > 0 ? 'success' : 'error'}
                                                            variant="outlined"
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {slot.totalQualifiedInspectors} Total
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        No available time slots found for the selected date.
                                    </Alert>
                                )}
                            </>
                        )}
                    </Grid>

                    {/* Inspector Selection */}
                    {selectedTimeSlot && (
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel id="inspector-label">Available Inspectors</InputLabel>
                                <Select
                                    labelId="inspector-label"
                                    value={inspector}
                                    label="Available Inspectors"
                                    onChange={(e) => setInspector(e.target.value)}
                                    disabled={fetchingInspectors}
                                    size="medium"
                                >
                                    {fetchingInspectors ? (
                                        <MenuItem disabled>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Loading inspectors...
                                        </MenuItem>
                                    ) : inspectors.length === 0 ? (
                                        <MenuItem disabled>
                                            No available inspectors for selected time slot
                                        </MenuItem>
                                    ) : (
                                        inspectors.map((insp) => (
                                            <MenuItem key={insp.inspectorID} value={insp.inspectorID}>
                                                {insp.name} ({insp.email})
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Display Selected Details */}
                    {selectedTimeSlot && inspector && (
                        <Grid item xs={12}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                    New Assignment Summary
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            <strong>New Date:</strong> {format(new Date(selectedDate), 'EEEE, MMMM dd, yyyy')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            <strong>New Time Slot:</strong> {selectedTimeSlot ? formatTimeSlot(selectedTimeSlot.startTime, selectedTimeSlot.endTime) : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            <strong>New Inspector:</strong> {inspectors.find(insp => insp.inspectorID === Number(inspector))?.name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Alert>
                        </Grid>
                    )}

                    {/* Notes */}
                    <Grid item xs={12}>
                        <TextField
                            label="Notes for Inspector"
                            multiline
                            rows={3}
                            fullWidth
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add any special instructions or details for the inspector..."
                            size="medium"
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 },
                alignItems: 'stretch'
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="secondary"
                    fullWidth={window.innerWidth < 600}
                    sx={{ order: { xs: 2, sm: 1 } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleReassignInspector}
                    variant="contained"
                    color="warning"
                    disabled={!selectedTimeSlot || !inspector || !selectedDate}
                    fullWidth={window.innerWidth < 600}
                    sx={{ order: { xs: 1, sm: 2 } }}
                >
                    Reassign Inspector
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReassignInspectorModal;